export const longevityCategories = ['strength', 'mobility', 'stability', 'recovery'] as const
export type LongevityCategory = typeof longevityCategories[number]
export interface CategoryParameterMapping { parameter: string; category: string }
export type ParameterMapping = { total: string; product: string } & Record<LongevityCategory, CategoryParameterMapping>
export interface ScoreRange { min: number; max: number }
export interface CategoryRating { category: string; rating: number }

function textParameter(params: URLSearchParams, name: string): string | null {
	if (!name.trim()) return null
	const values = params.getAll(name.trim())
	if (values.length !== 1) return null
	return values[0].trim() || null
}

function integerValue(raw: string | null, max: number): number | null {
	if (raw === null || !/^\d+$/.test(raw)) return null
	const value = Number(raw)
	return Number.isInteger(value) && value >= 0 && value <= max ? value : null
}

function categoryTier(params: URLSearchParams, mapping: CategoryParameterMapping): number | null {
	const tiers = textParameter(params, mapping.parameter)
	const category = mapping.category.trim()
	if (tiers === null || !category) return null
	// URLSearchParams decodes once. Unknown categories never influence a configured category.
	const matches = tiers.split(',').map((pair) => pair.split(':')).filter((pair) => pair[0].trim() === category)
	if (matches.length !== 1 || matches[0].length !== 2) return null
	return integerValue(matches[0][1].trim(), 5)
}

export function readQuizResults(params: URLSearchParams, mapping: Pick<ParameterMapping, 'total' | 'product'>) {
	return {
		total: integerValue(textParameter(params, mapping.total), 100),
		product: textParameter(params, mapping.product),
	}
}

export function readResults(params: URLSearchParams, mapping: ParameterMapping) {
	return {
		...readQuizResults(params, mapping),
		ratings: Object.fromEntries(longevityCategories.map((category) => [
			category, categoryTier(params, mapping[category]),
		])) as Record<LongevityCategory, number | null>,
	}
}

function validRange(range: ScoreRange): boolean {
	return Number.isFinite(range.min) && Number.isFinite(range.max) && range.min >= 0 && range.max <= 100 && range.min <= range.max
}

// Inclusive boundaries. Configuration order is the deterministic tie-breaker.
export function matchScoreRange<T extends ScoreRange>(score: number | null, ranges: T[]): T | null {
	if (score === null || !Number.isFinite(score) || score < 0 || score > 100) return null
	return ranges.find((range) => validRange(range) && score >= range.min && score <= range.max) ?? null
}

export function matchCategoryBlock<T extends CategoryRating>(category: LongevityCategory, rating: number | null, blocks: T[]): T | null {
	if (rating === null || !Number.isInteger(rating) || rating < 0 || rating > 5) return null
	return blocks.find((block) => block.category === category && block.rating === rating) ?? null
}

// These warnings are shown only in the theme editor, never logged with quiz data.
export function configurationIssues(ranges: ScoreRange[], blocks: CategoryRating[]): string[] {
	const issues: string[] = []
	ranges.forEach((range, index) => {
		if (!validRange(range)) issues.push(`Description range ${index + 1} is invalid: use a beginning at or below the end, within 0–100.`)
		else ranges.slice(0, index).forEach((previous, previousIndex) => {
			if (validRange(previous) && range.min <= previous.max && previous.min <= range.max) {
				issues.push(`Description ranges ${previousIndex + 1} and ${index + 1} overlap. The first configured description wins.`)
			}
		})
	})
	longevityCategories.forEach((category) => {
		if (!blocks.some((block) => block.category === category)) issues.push(`Add the ${category} category block.`)
	})
	return issues
}
